<template>
  <div class="dense content-box alternate text-left">
    <div class="sidebar-filter">
      <div class="d-flex sidebar-filter__head align-center">
        <div class="d-flex flex-grow-1 sidebar-filter__title align-center">
          <v-icon class="mr-2 large" color="primary">mdi-tune</v-icon>
          <h2>Filter</h2>
        </div>
        <div>
          <v-btn
            small
            fab
            color="primary"
            @click="clearAllFilters"
            :title="$t('jobs.removeAllFilters')"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <v-row v-if="filters.includes('company-name')" class="mt-8">
        <v-col cols="12">
          <h3 class="sidebar-filter__filter-label">{{ $t('jobs.company') }}</h3>
          <ul class="sidebar-filter__options">
            <li
              v-for="company in sortedCompanies"
              :key="`company_filter_${company.id}`"
            >
              <v-checkbox
                v-model="companyFilters"
                :label="`${localize(company.name)} (${company.numJobs})`"
                :aria-label="`${$t('jobs.company')} ${localize(company.name)}`"
                :aria-checked="
                  companyFilters.includes(company) ? 'true' : 'false'
                "
                :value="company"
                hide-details
              >
              </v-checkbox>
            </li>
          </ul>
        </v-col>
      </v-row>

      <v-row v-if="filters.includes('locations')" class="mt-8">
        <v-col cols="12">
          <h3 class="sidebar-filter__filter-label">
            {{ $t('jobs.location') }}
          </h3>
          <ul class="sidebar-filter__options">
            <li
              v-for="location in sortedLocations"
              :key="`location_filter_${location.id}`"
            >
              <v-checkbox
                v-model="locationFilters"
                :label="`${localize(location.name)} (${location.numJobs})`"
                :value="location"
                :aria-checked="
                  locationFilters.includes(location) ? 'true' : 'false'
                "
                :aria-label="`${$t('jobs.location')} ${localize(
                  location.name
                )}`"
                hide-details
              >
              </v-checkbox>
            </li>
          </ul>
        </v-col>
      </v-row>

      <v-row v-if="filters.includes('category')" class="mt-8">
        <v-col cols="12">
          <h3 class="sidebar-filter__filter-label">
            {{ $t('jobs.category') }}
          </h3>
          <ul class="sidebar-filter__options">
            <li
              v-for="category in sortedCategories"
              :key="`category_filter_${category.id}`"
            >
              <v-checkbox
                v-model="categoryFilters"
                :label="`${localize(category.name)} (${category.numJobs})`"
                :value="category"
                :aria-checked="
                  categoryFilters.includes(category) ? 'true' : 'false'
                "
                :aria-label="`${$t('jobs.category')} ${localize(
                  category.name
                )}`"
                hide-details
              >
              </v-checkbox>
            </li>
          </ul>
        </v-col>
      </v-row>

      <v-row v-if="filters.includes('departments')" class="mt-4">
        <v-col cols="12">
          <h3 class="sidebar-filter__filter-label">
            {{ $t('jobs.department') }}
          </h3>
          <ul class="sidebar-filter__options">
            <li
              v-for="department in sortedDepartments"
              :key="`department_filter_${department.id}`"
            >
              <v-checkbox
                v-model="departmentFilters"
                :label="`${localize(department.name)} (${department.numJobs})`"
                :value="department"
                :aria-label="`${$t('jobs.department')} ${localize(
                  department.name
                )}`"
                :aria-checked="
                  departmentFilters.includes(department) ? 'true' : 'false'
                "
                hide-details
              ></v-checkbox>
            </li>
          </ul>
        </v-col>
      </v-row>

      <v-row v-if="filters.includes('contract-types')" class="mt-4">
        <v-col cols="12">
          <h3 class="sidebar-filter__filter-label">
            {{ $t('jobs.contractType') }}
          </h3>
          <ul class="sidebar-filter__options">
            <li
              v-for="contractType in sortedContractTypes"
              :key="`contract_type_filter_${contractType.id}`"
            >
              <v-checkbox
                v-model="contractTypeFilters"
                :label="`${localize(contractType.name)} (${
                  contractType.numJobs
                })`"
                :value="contractType"
                :aria-label="`${$t('jobs.contractType')} ${localize(
                  contractType.name
                )}`"
                :aria-checked="
                  contractTypeFilters.includes(contractType) ? 'true' : 'false'
                "
                hide-details
              ></v-checkbox>
            </li>
          </ul>
        </v-col>
      </v-row>

      <v-row v-if="filters.includes('career-levels')" class="mt-4">
        <v-col cols="12">
          <h3 class="sidebar-filter__filter-label">
            {{ $t('jobs.careerLevel') }}
          </h3>
          <ul class="sidebar-filter__options">
            <li
              v-for="careerLevel in sortedCareerLevels"
              :key="`career_level_filter_${careerLevel.id}`"
            >
              <v-checkbox
                v-model="careerLevelFilters"
                :label="`${localize(careerLevel.name)} (${
                  careerLevel.numJobs
                })`"
                :value="careerLevel"
                :aria-label="`${$t('jobs.careerLevel')} ${localize(
                  careerLevel.name
                )}`"
                :aria-checked="
                  careerLevelFilters.includes(careerLevel) ? 'true' : 'false'
                "
                hide-details
              ></v-checkbox>
            </li>
          </ul>
        </v-col>
      </v-row>

      <v-row v-if="filters.includes('working-times')" class="mt-4">
        <v-col cols="12">
          <h3 class="sidebar-filter__filter-label">
            {{ $t('jobs.workingTime') }}
          </h3>
          <ul class="sidebar-filter__options">
            <li
              v-for="workingTime in sortedWorkingTimes"
              :key="`working_time_filter_${workingTime.id}`"
            >
              <v-checkbox
                v-model="workingTimeFilters"
                :label="`${localize(workingTime.name)} (${
                  workingTime.numJobs
                })`"
                :value="workingTime"
                :aria-label="`${$t('jobs.workingTime')} ${localize(
                  workingTime.name
                )}`"
                :aria-checked="
                  workingTimeFilters.includes(workingTime) ? 'true' : 'false'
                "
                hide-details
              ></v-checkbox>
            </li>
          </ul>
        </v-col>
      </v-row>

      <v-row v-if="filters.includes('cost-centers')" class="mt-4">
        <v-col cols="12">
          <h3 class="sidebar-filter__filter-label">
            {{ $t('jobs.costCenter') }}
          </h3>
          <ul class="sidebar-filter__options">
            <li
              v-for="costCenter in sortedCostCenters"
              :key="`cost_center_filter_${costCenter.id}`"
            >
              <v-checkbox
                v-model="costCenterFilters"
                :label="`${localize(costCenter.name)} (${costCenter.numJobs})`"
                :value="costCenter"
                :aria-label="`${$t('jobs.costCenter')} ${localize(
                  costCenter.name
                )}`"
                :aria-checked="
                  costCenterFilters.includes(costCenter) ? 'true' : 'false'
                "
                hide-details
              ></v-checkbox>
            </li>
          </ul>
        </v-col>
      </v-row>

      <v-row v-if="filters.includes('temp-jobs')" class="mt-4">
        <v-col cols="12">
          <h3 class="sidebar-filter__filter-label">{{ $t('jobs.tempJob') }}</h3>
          <ul class="sidebar-filter__options">
            <li
              v-for="tempJob in sortedTempJobs"
              :key="`temp_job_filter_${tempJob.id}`"
            >
              <v-checkbox
                v-model="tempJobFilters"
                :label="`${localize(tempJob.name)} (${tempJob.numJobs})`"
                :value="tempJob"
                :aria-label="`${$t('jobs.tempJob')} ${localize(tempJob.name)}`"
                :aria-checked="
                  tempJobFilters.includes(tempJob) ? 'true' : 'false'
                "
                hide-details
              ></v-checkbox>
            </li>
          </ul>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { eventBus } from '@/event-bus.js';

export default {
  data() {
    return {
      showDialog: false,
      companyFilters: [],
      locationFilters: [],
      departmentFilters: [],
      careerLevelFilters: [],
      workingTimeFilters: [],
      categoryFilters: [],
      contractTypeFilters: [],
      costCenterFilters: [],
      tempJobFilters: []
    };
  },

  computed: {
    ...mapState('jobsModule', {
      activeFilters: 'filters'
    }),

    ...mapGetters('jobsModule', {
      locations: 'getLocations',
      departments: 'getDepartments',
      careerLevels: 'getCareerLevels',
      workingTimes: 'getWorkingTimes',
      categories: 'getCategories',
      contractTypes: 'getContractTypes',
      costCenters: 'getCostCenters',
      companies: 'getCompanies',
      tempJobs: 'getTempJobs'
    }),

    ...mapState('companyModule', {
      filters: 'filters'
    }),

    hasActiveFilters() {
      return Object.entries(this.activeFilters).length > 0;
    },

    sortedCareerLevels() {
      return [...this.careerLevels].sort((a, b) =>
        this.localize(a.name).localeCompare(this.localize(b.name))
      );
    },

    sortedCategories() {
      return [...this.categories].sort((a, b) =>
        this.localize(a.name).localeCompare(this.localize(b.name))
      );
    },

    sortedCompanies() {
      return [...this.companies].sort((a, b) =>
        this.localize(a.name).localeCompare(this.localize(b.name))
      );
    },

    sortedContractTypes() {
      return [...this.contractTypes].sort((a, b) =>
        this.localize(a.name).localeCompare(this.localize(b.name))
      );
    },

    sortedDepartments() {
      return [...this.departments].sort((a, b) => {
        return this.localize(a.name).localeCompare(this.localize(b.name));
      });
    },

    sortedLocations() {
      return [...this.locations].sort((a, b) =>
        this.localize(a.name).localeCompare(this.localize(b.name))
      );
    },

    sortedWorkingTimes() {
      return [...this.workingTimes].sort((a, b) =>
        this.localize(a.name).localeCompare(this.localize(b.name))
      );
    },

    sortedCostCenters() {
      return [...this.costCenters].sort((a, b) =>
        this.localize(a.name).localeCompare(this.localize(b.name))
      );
    },

    sortedTempJobs() {
      return [...this.tempJobs].sort((a, b) =>
        this.localize(a.name).localeCompare(this.localize(b.name))
      );
    }
  },

  watch: {
    companyFilters() {
      this.clearFilters('companyId');

      for (const company of this.companyFilters) {
        this.setFilter({
          field: 'companyId',
          value: company.id,
          name: company.name
        });
      }
    },

    locationFilters() {
      this.clearFilters('locationId');

      for (const location of this.locationFilters) {
        this.setFilter({
          field: 'locationId',
          value: location.id,
          name: location.name
        });
      }
    },

    departmentFilters() {
      this.clearFilters('departmentId');

      for (const department of this.departmentFilters) {
        this.setFilter({
          field: 'departmentId',
          value: department.id,
          name: department.name
        });
      }
    },

    careerLevelFilters() {
      this.clearFilters('careerLevelId');

      for (const careerLevel of this.careerLevelFilters) {
        this.setFilter({
          field: 'careerLevelId',
          value: careerLevel.id,
          name: careerLevel.name
        });
      }
    },

    categoryFilters() {
      this.clearFilters('categoryId');

      for (const category of this.categoryFilters) {
        this.setFilter({
          field: 'categoryId',
          value: category.id,
          name: category.name
        });
      }
    },

    contractTypeFilters() {
      this.clearFilters('contractTypeId');

      for (const contractType of this.contractTypeFilters) {
        this.setFilter({
          field: 'contractTypeId',
          value: contractType.id,
          name: contractType.name
        });
      }
    },

    workingTimeFilters() {
      this.clearFilters('workingTimeId');

      for (const workingTime of this.workingTimeFilters) {
        this.setFilter({
          field: 'workingTimeId',
          value: workingTime.id,
          name: workingTime.name
        });
      }
    },

    costCenterFilters() {
      this.clearFilters('costCenterId');

      for (const costCenter of this.costCenterFilters) {
        this.setFilter({
          field: 'costCenterId',
          value: costCenter.id,
          name: costCenter.name
        });
      }
    },

    tempJobFilters() {
      this.clearFilters('tempJobId');

      for (const tempJob of this.tempJobFilters) {
        this.setFilter({
          field: 'tempJobId',
          value: tempJob.id,
          name: tempJob.name
        });
      }
    }
  },

  mounted() {
    eventBus.$on('filtersCleared', this.onFiltersCleared);
    eventBus.$on('filterAdded', this.onFilterAdded);
    this.setCurrentFilters();
  },

  methods: {
    ...mapActions('jobsModule', {
      clearFilters: 'clearFilters',
      setFilter: 'setFilter'
    }),

    clearAllFilters() {
      this.clearFilters();
    },

    onFiltersCleared(field) {
      if (!field) {
        this.companyFilters = [];
        this.locationFilters = [];
        this.departmentFilters = [];
        this.careerLevelFilters = [];
        this.workingTimeFilters = [];
        this.categoryFilters = [];
        this.contractTypeFilters = [];
        this.costCenterFilters = [];
        this.tempJobFilters = [];
      }
    },

    onFilterAdded(filter) {
      if (filter.field === 'companyId') {
        const items = this.companies.filter((x) => x.id === filter.value);
        if (items.length) {
          this.companyFilters.push(...items);
        }
      }

      if (filter.field === 'categoryId') {
        const items = this.categories.filter((x) => x.id === filter.value);
        if (items.length) {
          this.categoryFilters.push(...items);
        }
      }

      if (filter.field === 'departmentId') {
        const items = this.departments.filter((x) => x.id === filter.value);
        if (items.length) {
          this.departmentFilters.push(...items);
        }
      }

      if (filter.field === 'careerLevelId') {
        const items = this.careerLevels.filter((x) => x.id === filter.value);
        if (items.length) {
          this.careerLevelFilters.push(...items);
        }
      }

      if (filter.field === 'contractTypeId') {
        const items = this.contractTypes.filter((x) => x.id === filter.value);
        if (items.length) {
          this.contractTypeFilters.push(...items);
        }
      }
    },

    setCurrentFilters() {
      if (!this.filters || !this.activeFilters) {
        return;
      }

      this.setCurrentFilter(
        'company-name',
        'companyId',
        'companyFilters',
        'companies'
      );

      this.setCurrentFilter(
        'departments',
        'departmentId',
        'departmentFilters',
        'departments'
      );

      this.setCurrentFilter(
        'locations',
        'locationId',
        'locationFilters',
        'locations'
      );

      this.setCurrentFilter(
        'category',
        'categoryId',
        'categoryFilters',
        'categories'
      );

      this.setCurrentFilter(
        'cost-centers',
        'costCenterId',
        'costCenterFilters',
        'costCenters'
      );

      this.setCurrentFilter(
        'temp-jobs',
        'tempJobId',
        'tempJobFilters',
        'tempJobs'
      );
    },

    setCurrentFilter(filterName, filterKey, filterList, filterOptions) {
      if (!this.filters.includes(filterName)) {
        return;
      }

      const activeFilters = this.activeFilters[filterKey];
      if (!activeFilters) {
        return;
      }

      const filterValues = activeFilters.map((x) => x.value);
      this[filterList] = this[filterOptions].filter((x) =>
        filterValues.includes(x.id)
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.sidebar-filter__options {
  margin: 0;
  padding: 0;
  list-style: none;
}

h2,
h3 {
  font-family: var(--font-family) !important;
  font-size: 1rem !important;
  color: var(--font-color) !important;
  margin-bottom: 0 !important;
  line-height: 1.5 !important;
}

h2 {
  font-weight: 300 !important;
}

h3 {
  font-weight: 700 !important;
}
</style>
